import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useState, useRef } from "react";
import { Helmet } from "react-helmet-async";

interface Product {
  id: "sweet" | "salt";
  name: string;
  price: string;
  quantity: string;
  ingredients: string[];
  images: string[];
}

const sweetImages = [
  "/images/Gemini_Generated_Image_bzang6bzang6bzan.png",
  "/images/Gemini_Generated_Image_f8jyu9f8jyu9f8jy.png",
];

const saltImages = [
  "/images/Gemini_Generated_Image_9rcmeu9rcmeu9rcm.png",
  "/images/Gemini_Generated_Image_7jf8rg7jf8rg7jf8.png",
];

const PRODUCTS: Product[] = [
  {
    id: "sweet",
    name: "Sweet Ganji",
    price: "₹25",
    quantity: "250ml",
    ingredients: ["ragi", "jaggery", "cardamom"],
    images: sweetImages,
  },
  {
    id: "salt",
    name: "Salt Ganji",
    price: "₹25",
    quantity: "250ml",
    ingredients: ["ragi", "salt"],
    images: saltImages,
  },
];

interface ProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ProductModal({ open, onOpenChange }: ProductModalProps) {
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [imageActive, setImageActive] = useState<Record<string, number>>({ sweet: 0, salt: 0 });
  const imageRowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const toggle = (id: string) => {
    setSelected((s) => {
      const next = !s[id];
      if (next) {
        setQuantities((q) => ({ ...q, [id]: q[id] ?? 1 }));
      } else {
        setQuantities((q) => {
          const { [id]: _, ...rest } = q;
          return rest;
        });
      }
      return { ...s, [id]: next };
    });
  };

  const selectedProducts = PRODUCTS.filter((p) => selected[p.id]);

  const handleBuy = () => {
    if (selectedProducts.length === 0) return;
    const lines = selectedProducts.map(
      (p) => `${p.name} x${quantities[p.id] ?? 1} (${p.price}, ${p.quantity}; ingredients: ${p.ingredients.join(", ")})`
    );
    const message = encodeURIComponent(
      `Hello! I'd like to buy: ${lines.join("; ")}.\nPlease confirm availability.`
    );
    const url = `https://wa.me/919019323880?text=${message}`;
    window.open(url, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": "Ganji Traditional Ragi Drink",
            "image": [
              "https://ganjidrinks.shop/images/Gemini_Generated_Image_bzang6bzang6bzan.png",
              "https://ganjidrinks.shop/images/Gemini_Generated_Image_f8jyu9f8jyu9f8jy.png",
              "https://ganjidrinks.shop/images/Gemini_Generated_Image_9rcmeu9rcmeu9rcm.png",
              "https://ganjidrinks.shop/images/Gemini_Generated_Image_7jf8rg7jf8rg7jf8.png"
            ],
            "description": "Authentic traditional ragi-based drink with natural ingredients. Available in Sweet and Salt varieties.",
            "brand": {
              "@type": "Brand",
              "name": "Ganji Drinks",
              "url": "https://ganjidrinks.shop",
              "logo": "https://ganjidrinks.shop/images/ganjidrinks-logo.png"
            },
            "category": "Healthy ragi beverage",
            "offers": [
              {
                "@type": "Offer",
                "url": "https://ganjidrinks.shop",
                "priceCurrency": "INR",
                "price": "25",
                "priceValidUntil": "2026-12-31",
                "itemCondition": "https://schema.org/NewCondition",
                "availability": "https://schema.org/InStock",
                "seller": {
                  "@type": "Organization",
                  "name": "Ganji Drinks"
                }
              }
            ],
            "nutrition": {
              "@type": "NutritionInformation",
              "calories": "45",
              "proteinContent": "1.2",
              "carbohydrateContent": "8.5",
              "fatContent": "0.3",
              "fiberContent": "2.1",
              "sugarContent": "3.2"
            },
            "additionalProperty": [
              {
                "@type": "PropertyValue",
                "name": "Main Ingredient",
                "value": "Ragi (Finger Millet)"
              },
              {
                "@type": "PropertyValue",
                "name": "Gluten-Free",
                "value": "Yes"
              },
              {
                "@type": "PropertyValue",
                "name": "Storage Instructions",
                "value": "Refrigerate after opening, consume within 24 hours"
              }
            ]
          })}
        </script>
      </Helmet>
      <DialogContent
        className="max-w-2xl border-2 shadow-2xl"
        aria-describedby={undefined}
        style={{
          background:
            "linear-gradient(135deg, hsl(var(--ganji-warm-cream)) 0%, hsl(38 52% 89%) 100%)",
          borderColor: "hsl(var(--ganji-rustic-brown))",
          color: "hsl(var(--ganji-rustic-brown))",
          boxShadow: "0 24px 80px hsl(var(--ganji-rustic-brown) / 0.28)",
        }}
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: "'Permanent Marker', cursive",
              color: "hsl(var(--ganji-rustic-brown))",
            }}
          >
            Choose Your Ganji
          </DialogTitle>
          <DialogDescription
            style={{
              fontFamily: "'Patrick Hand', cursive",
              color: "hsl(var(--ganji-rustic-brown) / 0.9)",
            }}
          >
            Select one or both. We'll prepare a WhatsApp message with your selection.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={rowRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory py-2 no-scrollbar"
          style={{ touchAction: "pan-x", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none" }}
          onScroll={() => {
            const row = rowRef.current;
            if (!row) return;
            const rect = row.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            let best = 0;
            let bestDist = Number.POSITIVE_INFINITY;
            for (let i = 0; i < PRODUCTS.length; i++) {
              const el = cardRefs.current[i];
              if (!el) continue;
              const cr = el.getBoundingClientRect();
              const c = cr.left + cr.width / 2;
              const d = Math.abs(c - centerX);
              if (d < bestDist) {
                bestDist = d;
                best = i;
              }
            }
            setActiveIndex(best);
          }}
        >
          {PRODUCTS.map((p, idx) => (
            <Card
              key={p.id}
              className={cn(
                "min-w-[280px] snap-center border-2",
                "shadow-xl",
                "hover-elevate active-elevate-2"
              )}
              style={{
                background: "hsl(var(--ganji-warm-cream))",
                borderColor: "hsl(var(--ganji-rustic-brown))",
                color: "hsl(var(--ganji-rustic-brown))",
                borderRadius: "0.75rem",
              }}
              ref={(el) => (cardRefs.current[idx] = el)}
            >
              <div className="p-4 flex flex-col gap-3">
                <div
                  ref={(el) => (imageRowRefs.current[p.id] = el)}
                  className="flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar"
                  style={{ touchAction: "pan-x", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none" }}
                  onScroll={() => {
                    const row = imageRowRefs.current[p.id];
                    if (!row) return;
                    const rect = row.getBoundingClientRect();
                    const centerX = rect.left + rect.width / 2;
                    let best = 0;
                    let bestDist = Number.POSITIVE_INFINITY;
                    const imgs = row.querySelectorAll('img');
                    imgs.forEach((img, i) => {
                      const r = (img as HTMLImageElement).getBoundingClientRect();
                      const c = r.left + r.width / 2;
                      const d = Math.abs(c - centerX);
                      if (d < bestDist) {
                        bestDist = d;
                        best = i;
                      }
                    });
                    setImageActive((prev) => ({ ...prev, [p.id]: best }));
                  }}
                >
                  {p.images.map((src, idx) => (
                    <img
                      key={idx}
                      src={src}
                      alt={`${p.name} bottle view ${idx + 1} - Traditional Ragi Drink`}
                      className="w-56 h-40 object-cover rounded-md snap-center flex-none"
                      width={224}
                      height={160}
                      style={{ boxShadow: "2px 2px 0 hsl(var(--ganji-rustic-brown) / 0.25)" }}
                      loading="lazy"
                    />
                  ))}
                </div>
                <div className="mt-1 flex justify-center">
                  {p.images.map((_, i) => (
                    <span
                      key={i}
                      className="mx-1 h-2 w-2 rounded-full"
                      style={{
                        background:
                          imageActive[p.id] === i
                            ? "hsl(var(--ganji-rustic-brown))"
                            : "hsl(var(--ganji-rustic-brown) / 0.35)",
                      }}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <span
                    className="text-xl font-bold"
                    style={{ fontFamily: "'Permanent Marker', cursive" }}
                  >
                    {p.name}
                  </span>
                  <Checkbox checked={!!selected[p.id]} onCheckedChange={() => toggle(p.id)} />
                </div>

                <div className="flex items-center gap-3 text-sm">
                  <span className="font-semibold">{p.price}</span>
                  <span>•</span>
                  <span>{p.quantity}</span>
                </div>

                <div className="text-sm">
                  <span className="font-semibold">Ingredients:</span> {p.ingredients.join(", ")}
                </div>

                {selected[p.id] && (
                  <div className="flex items-center gap-3 pt-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantities((q) => ({ ...q, [p.id]: Math.max(1, (q[p.id] ?? 1) - 1) }))}
                      className="h-8 w-8"
                    >
                      -
                    </Button>
                    <span className="min-w-8 text-center font-bold" style={{ fontFamily: "'Permanent Marker', cursive" }}>
                      {quantities[p.id] ?? 1}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantities((q) => ({ ...q, [p.id]: Math.min(99, (q[p.id] ?? 1) + 1) }))}
                      className="h-8 w-8"
                    >
                      +
                    </Button>
                    <span className="text-sm">bottles</span>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
        <div className="mt-2 flex justify-center md:hidden">
          {PRODUCTS.map((_, i) => (
            <span
              key={i}
              className="mx-1 h-2 w-2 rounded-full"
              style={{
                background:
                  i === activeIndex
                    ? "hsl(var(--ganji-rustic-brown))"
                    : "hsl(var(--ganji-rustic-brown) / 0.35)",
              }}
            />
          ))}
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleBuy}
            disabled={selectedProducts.length === 0}
            className="relative px-6 py-3 font-bold border-2"
            style={{
              fontFamily: "'Permanent Marker', cursive",
              background: "hsl(var(--ganji-warm-cream))",
              color: "hsl(var(--ganji-rustic-brown))",
              borderColor: "hsl(var(--ganji-rustic-brown))",
              borderRadius: "0.5rem",
              transform: "rotate(-1deg)",
              boxShadow: "4px 4px 0 hsl(var(--ganji-rustic-brown) / 0.3)",
            }}
          >
            Buy Selected
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

